/**
 * Input quality validation for assessment pages.
 * Catches gibberish, non-answers, profanity, prompt injection, and lazy inputs.
 */

/* ── Prompt injection detection ──────────────────────────────────────────────
 * Catches attempts to hijack the AI evaluator by injecting instructions
 * into assessment answers or focus topic fields.
 */
const INJECTION_PATTERNS = [
  /ignore\s+(all\s+)?(previous|prior|above|earlier|preceding)\s+(instructions?|prompts?|rules?|directions?)/i,
  /disregard\s+(all\s+)?(previous|prior|above|earlier|preceding)/i,
  /forget\s+(all\s+)?(previous|prior|above|your)\s+(instructions?|prompts?|rules?|context)/i,
  /you\s+are\s+now\s+(a|an|in|my)/i,
  /new\s+instruction[s:]?\s/i,
  /override\s+(system|instructions?|rules?|prompt)/i,
  /\bsystem\s*prompt\b/i,
  /\bact\s+as\s+(a|an|if|though)\b/i,
  /\bpretend\s+(you|to\s+be|you'?re)\b/i,
  /\brole\s*play\s+as\b/i,
  /\bdo\s+not\s+follow\s+(your|the|any)\b/i,
  /\bjailbreak\b/i,
  /\bDAN\b/, // "Do Anything Now" jailbreak
  /\bdeveloper\s+mode\b/i,
  /\bbypass\s+(filter|safety|guard|restrict)/i,
  /\bunrestricted\s+mode\b/i,
  /\brepeat\s+(back|after|everything)\b/i,
  /\bprint\s+(your|the|all)\s+(instructions?|prompt|rules?|system)/i,
  /\bshow\s+(me\s+)?(your|the)\s+(instructions?|prompt|rules?|system)/i,
  /\bwhat\s+(are|is)\s+your\s+(instructions?|prompt|rules?|system\s+prompt)/i,
  /\boutput\s+(your|the)\s+(instructions?|prompt|system)/i,
  /\b(reveal|leak|expose|dump)\s+(your|the|system)/i,
  /\bignore\s+(this|that|the)\s+(assessment|evaluation|test|quiz)/i,
  /\breturn\s+(only|just)\s/i,
  /\brespond\s+with\s+(only|just)\s/i,
  /\bsay\s+(only|just|exactly)\s/i,
  /```[\s\S]*system/i, // code blocks trying to inject system instructions
  /<\/?(?:system|instruction|prompt|injection)/i, // XML-tag injection attempts
];

function containsInjection(text) {
  const lower = text.toLowerCase();
  return INJECTION_PATTERNS.some((re) => re.test(lower));
}

const NON_ANSWERS = new Set([
  'asdf', 'fdsa', 'qwer', 'rewq', 'zxcv', 'qwerty', 'wasd',
  'test', 'testing', 'tester', 'testt',
  'n/a', 'na', 'none', 'nothing', 'nope', 'no', 'yes', 'yeah',
  'idk', 'dk', 'dunno', 'dontknow',
  'ok', 'okay', 'sure', 'fine', 'whatever', 'meh', 'blah',
  'hello', 'hi', 'hey', 'sup', 'yo',
  'lol', 'lmao', 'rofl', 'bruh', 'bro',
  'abc', 'abcd', 'xyz', 'foo', 'bar', 'baz',
  'stuff', 'things', 'something', 'anything', 'everything',
  'aaa', 'bbb', 'zzz', 'xxx', 'yyy',
  'pass', 'skip', 'next', 'blank', 'empty',
]);

const PROFANITY = new Set([
  'fuck', 'fucking', 'fucked', 'fucker', 'fucks',
  'shit', 'shitty', 'bullshit',
  'ass', 'asshole', 'bitch', 'bitches',
  'damn', 'damned', 'damnit',
  'dick', 'dicks', 'cock', 'cocks',
  'cunt', 'cunts',
  'piss', 'pissed',
  'bastard', 'bastards',
  'whore', 'slut',
  'nigger', 'nigga', 'faggot', 'fag', 'retard', 'retarded',
]);

function stripToAlpha(text) {
  return text.toLowerCase().replace(/[^a-z]/g, '');
}

function hasLowCharDiversity(text) {
  const alpha = stripToAlpha(text);
  if (alpha.length < 3) return false;
  const unique = new Set(alpha);
  return unique.size <= 2;
}

function isAllRepeatedWords(words) {
  if (words.length < 2) return false;
  const unique = new Set(words.map((w) => w.toLowerCase()));
  return unique.size === 1;
}

function containsProfanity(words) {
  return words.some((w) => PROFANITY.has(stripToAlpha(w)));
}

/**
 * Validate a focus topic input (lighter rules).
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateFocusTopic(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { valid: false, reason: '' }; // empty = no message needed, button already disabled

  const alpha = stripToAlpha(trimmed);
  if (alpha.length < 3) {
    return { valid: false, reason: 'Please describe your focus area in a few words.' };
  }

  if (NON_ANSWERS.has(alpha)) {
    return { valid: false, reason: 'Please enter a real area of interest — this helps the examiner tailor your assessment.' };
  }

  if (hasLowCharDiversity(trimmed)) {
    return { valid: false, reason: 'Please enter a real area of interest.' };
  }

  if (containsInjection(trimmed)) {
    return { valid: false, reason: 'Please enter a genuine area of interest — not instructions for the AI.' };
  }

  const words = trimmed.split(/\s+/).filter((w) => w.length > 0);
  if (containsProfanity(words)) {
    return { valid: false, reason: 'Please keep your response professional.' };
  }

  return { valid: true };
}

/**
 * Validate a placement assessment answer (stricter rules).
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateAssessmentAnswer(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return { valid: false, reason: '' }; // empty = no message

  const words = trimmed.split(/\s+/).filter((w) => w.length > 0);
  const alpha = stripToAlpha(trimmed);

  // Non-answer exact match
  if (NON_ANSWERS.has(alpha)) {
    return { valid: false, reason: 'Please provide a genuine, thoughtful response.' };
  }

  // Low character diversity (e.g., "aaaaaaaaa", "ababababab")
  if (hasLowCharDiversity(trimmed)) {
    return { valid: false, reason: 'Please provide a genuine response.' };
  }

  // Too short — need at least 3 words
  if (words.length < 3) {
    return { valid: false, reason: 'Your answer is too brief — write at least a full sentence.' };
  }

  // Too short by character count
  if (trimmed.length < 15) {
    return { valid: false, reason: 'Your answer is too brief — please elaborate.' };
  }

  // All repeated words (e.g., "test test test test")
  if (isAllRepeatedWords(words)) {
    return { valid: false, reason: 'Please provide a real answer — not repeated words.' };
  }

  // Prompt injection
  if (containsInjection(trimmed)) {
    return { valid: false, reason: 'Please provide a genuine assessment answer.' };
  }

  // Profanity
  if (containsProfanity(words)) {
    return { valid: false, reason: 'Please keep your response professional.' };
  }

  return { valid: true };
}
